import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeftIcon, ChevronRightIcon, XIcon, TicketIcon, TableIcon } from '@/components/icons';
import { generateCalendarDateString, formatTime, formatDateByTimezone } from '@/lib/utils';

interface EventSchedule {
  id: string;
  start_time: string;
  duration: number;
  event: {
    id: string;
    name: string;
    business?: {
      handle: string;
    };
  };
}

interface CalendarViewProps {
  availableDates: string[];
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  businessTimeZone?: string;
  schedules?: EventSchedule[];
  onBookTickets?: (schedule: EventSchedule) => void;
  onBookTables?: (schedule: EventSchedule) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
  availableDates, 
  selectedDate, 
  onDateSelect,
  businessTimeZone,
  schedules = [],
  onBookTickets,
  onBookTables
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  // Helper function for timezone-aware formatting
  const getMonthYear = (dateStr: string, businessTimeZone?: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      month: 'long', 
      year: 'numeric'
    };
    
    if (businessTimeZone) {
      options.timeZone = businessTimeZone;
    }
    // If no timezone is provided, let the browser use local timezone
    
    return new Date(dateStr).toLocaleDateString('en-US', options);
  };
  
  useEffect(() => {
    const firstAvailableDate = availableDates[0] ? new Date(availableDates[0]) : new Date();
    setCurrentMonth(firstAvailableDate);
  }, [availableDates]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const isDateAvailable = (day: number) => {
    const dateStr = generateCalendarDateString(
      currentMonth.getFullYear(), 
      currentMonth.getMonth() + 1, 
      day
    );
    return availableDates.includes(dateStr);
  };

  const isDateSelected = (day: number) => {
    const dateStr = generateCalendarDateString(
      currentMonth.getFullYear(), 
      currentMonth.getMonth() + 1, 
      day
    );
    return selectedDate === dateStr;
  };

  const getSchedulesForDay = (day: number) => {
    const dateStr = generateCalendarDateString(
      currentMonth.getFullYear(), 
      currentMonth.getMonth() + 1, 
      day
    );
    return schedules.filter(schedule => {
      const scheduleDate = formatDateByTimezone(schedule.start_time, businessTimeZone || 'Europe/London', { format: 'date' });
      return scheduleDate === dateStr;
    });
  };

  const handleDateClick = (day: number) => {
    const dateStr = generateCalendarDateString(
      currentMonth.getFullYear(), 
      currentMonth.getMonth() + 1, 
      day
    );
    if (isDateAvailable(day)) {
      onDateSelect(dateStr);
      const daySchedules = getSchedulesForDay(day);
      if (daySchedules.length > 0) {
        setSelectedDay(day);
        setShowModal(true);
      }
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleBookTickets = (schedule: EventSchedule) => {
    setShowModal(false);
    onBookTickets?.(schedule);
  };

  const handleBookTables = (schedule: EventSchedule) => {
    setShowModal(false);
    onBookTables?.(schedule);
  };

  const selectedDaySchedules = selectedDay ? getSchedulesForDay(selectedDay) : [];

  return (
    <>
      <div className="bg-gray-900/20 border border-gray-800/30 rounded-xl p-8 max-w-4xl mx-auto backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-800/30 rounded-md transition-colors"
          >
            <ChevronLeftIcon />
          </button>
          <h3 className="text-lg font-semibold text-gray-200">{getMonthYear(currentMonth.toISOString(), businessTimeZone)}</h3>
          <button 
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-800/30 rounded-md transition-colors"
          >
            <ChevronRightIcon />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-3 mb-6">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
            <div key={day} className="text-center text-lg text-gray-300 py-4 font-bold">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-3">
          {emptyDays.map(i => (
            <div key={`empty-${i}`} className="aspect-square"></div>
          ))}
          {days.map(day => {
            const daySchedules = getSchedulesForDay(day);
            const hasEvents = daySchedules.length > 0;
            
            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                disabled={!isDateAvailable(day)}
                className={`aspect-square flex flex-col items-center justify-start text-sm rounded-xl transition-all duration-300 relative group p-3 hover:scale-105 ${
                  isDateSelected(day)
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold shadow-xl shadow-blue-500/30 ring-2 ring-blue-400/50'
                    : isDateAvailable(day)
                    ? 'bg-gradient-to-br from-gray-800/40 to-gray-700/30 hover:from-gray-700/50 hover:to-gray-600/40 text-gray-200 cursor-pointer hover:text-white border border-gray-700/50 hover:border-gray-600/50'
                    : 'text-gray-600 cursor-not-allowed'
                }`}
              >
                <span className="text-lg font-bold mb-2">{day}</span>
                {hasEvents && (
                  <div className="flex flex-col items-center space-y-1 w-full flex-1 justify-center">
                    {daySchedules.slice(0, 2).map((schedule, idx) => (
                      <div key={idx} className="text-sm bg-gradient-to-r from-blue-500/30 to-blue-600/30 text-blue-200 px-2 py-1 rounded-lg text-center w-full font-medium border border-blue-400/20">
                        {formatTime(schedule.start_time, businessTimeZone)}
                      </div>
                    ))}
                    {daySchedules.length > 2 && (
                      <div className="text-sm text-gray-400 font-semibold">+{daySchedules.length - 2}</div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showModal && selectedDaySchedules.length > 0 && (
          <motion.div 
            className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div 
              className="bg-black/90 border border-gray-800 rounded-2xl w-full max-w-lg shadow-xl"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-800 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">
                    {formatDateByTimezone(
                      generateCalendarDateString(currentMonth.getFullYear(), currentMonth.getMonth() + 1, selectedDay!),
                      businessTimeZone || 'Europe/London',
                      { format: 'weekday' }
                    )}
                  </h3>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <XIcon />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {selectedDaySchedules.map((schedule) => (
                  <div key={schedule.id} className="bg-gray-900 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white text-sm truncate">{schedule.event.name}</h4>
                      <span className="text-gray-400 text-sm">
                        {formatTime(schedule.start_time, businessTimeZone)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBookTickets(schedule)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500 transition-colors"
                      >
                        <TicketIcon />
                        Tickets
                      </button>
                      <button
                        onClick={() => handleBookTables(schedule)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <TableIcon />
                        Tables
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CalendarView;
